import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { ArrowLeft, Save, EyeOff, Eye, CheckCircle2, Loader2, ImageIcon } from "lucide-react";

const PRODUCT_TYPES = ["Outerwear", "Dresses", "Tops", "Bottoms", "Tailoring", "Footwear", "Bags", "Accessories", "Knitwear", "Uncategorized"];

function Field({ label, value, onChange, testid, options }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#8E8E9A]">{label}</label>
      {options ? (
        <select
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-1 bg-[#0B0B0C] border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F8F6F0] focus:border-[#D4AF37] outline-none"
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      ) : (
        <input
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-1 bg-[#0B0B0C] border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F8F6F0] focus:border-[#D4AF37] outline-none"
        />
      )}
    </div>
  );
}

function ItemEditor({ item, lookThumbnails, onChanged }) {
  const [p, setP] = useState(item.product);
  const [hidden, setHidden] = useState(item.appearance.hidden);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/products/${p.id}`, {
        title: p.title,
        product_type: p.product_type,
        style_labels: typeof p.style_labels === "string" ? p.style_labels.split(",").map((s) => s.trim()).filter(Boolean) : p.style_labels,
        color: p.color,
        fabric: p.fabric,
        occasion: p.occasion,
        hero_thumbnail: p.hero_thumbnail,
      });
      toast.success("Product saved");
      onChanged();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleHide = async () => {
    const next = !hidden;
    setHidden(next);
    await api.patch(`/admin/appearances/${item.appearance.id}`, { hidden: next });
    toast(next ? "Detection hidden from publish" : "Detection restored");
    onChanged();
  };

  const styleStr = Array.isArray(p.style_labels) ? p.style_labels.join(", ") : p.style_labels;

  return (
    <div
      className={`rounded-sm border p-5 transition-all ${hidden ? "border-red-500/30 bg-red-500/5 opacity-70" : "border-white/10 bg-[#141417]"}`}
      data-testid={`item-editor-${p.id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {/* Thumbnail + crop selection */}
        <div className="md:col-span-3">
          <div className="aspect-[3/4] rounded-sm overflow-hidden bg-[#0B0B0C]">
            <img src={mediaUrl(p.hero_thumbnail)} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex items-center gap-1 mt-2">
            <span className="text-[9px] font-mono text-[#8E8E9A]">conf {(item.appearance.confidence * 100).toFixed(0)}%</span>
          </div>
          {lookThumbnails?.length > 1 && (
            <div className="flex gap-1 mt-2 flex-wrap" data-testid={`thumb-picker-${p.id}`}>
              {lookThumbnails.map((t, i) => (
                <button
                  key={i}
                  onClick={() => set("hero_thumbnail", t)}
                  data-testid={`thumb-option-${p.id}-${i}`}
                  className={`w-9 h-9 rounded-sm overflow-hidden border ${p.hero_thumbnail === t ? "border-[#D4AF37]" : "border-white/10 opacity-60"}`}
                >
                  <img src={mediaUrl(t)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Fields */}
        <div className="md:col-span-9 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Field label="Title" value={p.title} onChange={(v) => set("title", v)} testid={`edit-title-${p.id}`} />
          </div>
          <Field label="Product Type" value={p.product_type} onChange={(v) => set("product_type", v)} testid={`edit-type-${p.id}`} options={PRODUCT_TYPES} />
          <Field label="Style Labels (comma)" value={styleStr} onChange={(v) => set("style_labels", v)} testid={`edit-style-${p.id}`} />
          <Field label="Color" value={p.color} onChange={(v) => set("color", v)} testid={`edit-color-${p.id}`} />
          <Field label="Fabric" value={p.fabric} onChange={(v) => set("fabric", v)} testid={`edit-fabric-${p.id}`} />
          <Field label="Occasion" value={p.occasion} onChange={(v) => set("occasion", v)} testid={`edit-occasion-${p.id}`} />
          <div className="flex items-end gap-2">
            <button
              onClick={save}
              disabled={saving}
              data-testid={`save-product-${p.id}`}
              className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-sm text-xs font-medium hover:bg-[#C5A059] transition-colors"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
            </button>
            <button
              onClick={toggleHide}
              data-testid={`hide-item-${p.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-xs border transition-colors ${
                hidden ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400 hover:bg-red-500/10"
              }`}
            >
              {hidden ? <><Eye size={13} /> Restore</> : <><EyeOff size={13} /> Hide</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminLookReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/looks/${id}`).then((r) => {
      setData(r.data);
      setTitle(r.data.look.title);
    });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // poll while processing
  useEffect(() => {
    if (data?.look?.status === "processing") {
      const t = setInterval(load, 4000);
      return () => clearInterval(t);
    }
  }, [data?.look?.status, load]);

  if (!data) return <div className="overline animate-pulse">Loading…</div>;
  const { look, items } = data;

  const saveTitle = async () => {
    await api.patch(`/admin/looks/${id}`, { title });
    toast.success("Look title saved");
    load();
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post(`/admin/looks/${id}/publish`);
      toast.success("Look & products published to theluxbel.com");
      load();
    } catch (e) {
      toast.error("Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="fade-in">
      <button onClick={() => navigate("/admin/review")} className="inline-flex items-center gap-2 overline mb-6 hover:text-[#D4AF37]" data-testid="back-to-queue">
        <ArrowLeft size={13} /> Review Queue
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8 border-b border-white/10 pb-6">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-3 mb-3">
            <StatusBadge status={look.status} testid="look-status-badge" />
            <span className="font-mono text-xs text-[#8E8E9A]">{look.processing_message}</span>
          </div>
          <div className="flex items-center gap-3">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="look-title-input"
              className="bg-transparent border-b border-white/15 focus:border-[#D4AF37] outline-none font-serif text-3xl font-light py-1 flex-1"
            />
            <button onClick={saveTitle} data-testid="save-look-title" className="text-xs px-3 py-2 border border-white/15 rounded-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              Save
            </button>
          </div>
        </div>
        {look.status !== "processing" && (
          <button
            onClick={publish}
            disabled={publishing}
            data-testid="publish-look-button"
            className="flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 rounded-full text-sm font-medium hover:bg-[#C5A059] transition-colors disabled:opacity-50"
          >
            {publishing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
            {look.status === "published" ? "Re-publish" : "Approve & Publish"}
          </button>
        )}
      </div>

      {look.status === "processing" ? (
        <div className="flex flex-col items-center py-24 text-center">
          <Loader2 size={40} className="text-[#D4AF37] animate-spin" />
          <p className="font-serif text-2xl font-light mt-6">AI is analysing the film…</p>
          <p className="text-[#8E8E9A] text-sm mt-2">Extracting frames and detecting products. This page updates automatically.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center py-24 text-center">
          <ImageIcon size={36} className="text-[#8E8E9A]" />
          <p className="font-serif text-xl font-light mt-4">No products detected in this look.</p>
        </div>
      ) : (
        <>
          <p className="overline mb-5">{items.length} detected product(s) — edit, hide bad detections, then publish</p>
          <div className="space-y-4">
            {items.map((it) => (
              <ItemEditor key={it.appearance.id} item={it} lookThumbnails={look.thumbnails} onChanged={load} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
